import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { ComboBox } from 'cd-eft/components/common/ComboBox';
import { AdminErrorMessageVM } from './AdminErrorMessageVM';
import { Link, useHistory, useParams } from 'react-router-dom';
import { formatDateTime } from 'cd-eft/utils/formatDate';
import { Checkbox } from 'cd-eft/components/common/Checkbox';
import { BreadcrumbsControlled } from 'cd-eft/components/common/BreadcrumbsControlled';
import { FormFeedback, FormGroup, Label, Input, Spinner } from 'reactstrap';
import { StaticTooltip } from 'cd-eft/components/common/StaticTooltip/StaticTooltip';
import { Loader } from 'cd-eft/components/common/LoadingSpinner/LoadingSpinner';
import { mdiTrashCanOutline } from '@mdi/js';
import { DeleteElement } from 'cd-eft/components/Design/common/DeleteElement';
import Icon from '@mdi/react';
import './AdminErrorMessage.scss';

export const AdminErrorMessage = observer(() => {
  const history = useHistory();
  const vm = useRef(new AdminErrorMessageVM(history)).current;
  const { messageId }: any = useParams();

  useEffect(() => {
    if (messageId) {
      vm.init(messageId);
    } else {
      vm.initSelection();
    }
  }, [messageId]);

  useEffect(() => {
    if (vm.message.errorCode) {
      document.title = `NETS Admin Error Message ${vm.message.errorCode}`;
    } 
  }, [vm.message.errorCode]);

  return (
    <div className="admin-content">
      {vm.isLoading && <Loader message={vm.loadingMessage} />}

      <h1>Admin : Error Message</h1>

      <BreadcrumbsControlled crumbs={[{ description: 'Back', url: '/admin/error-messages' }]} />

      <div className="usa-card__container m-0 flex-fill">
        <div className="usa-card__header primary-card-header ">
          <h2>{messageId ? vm.message.errorCode : 'Add New Error Message'}</h2>

          {messageId ? (
            <button
              className="usa-button usa-button--unstyled"
              onClick={vm.toggleDeleteErrorMessageModalIsOpen}
              aria-label="Delete Error Message"
            >
              <Icon path={mdiTrashCanOutline} size={1} /> Delete
            </button>
          ) : null}
        </div>

        <div className="flex-fill p-3">
          <form onSubmit={vm.onSubmitForm}>
            <div className="d-flex justify-content-between flex-wrap flex-align-center">
              <div className="d-flex flex-wrap flex-fill">
                <div className="d-flex flex-wrap flex-align-top">
                  {messageId ? null : (
                    <>
                      <ComboBox
                        idAndName="errorCategoryCode"
                        label="Error Category"
                        // aria-label="Choose the Error Category"
                        required={true}
                        valid={true}
                        NoInitialBlankOption={false}
                        value={vm.message.errorCategoryCode}
                        onChange={vm.setValue}
                        options={vm.errorCategoryCodes}
                        errorMsg=""
                      />

                      <ComboBox
                        idAndName="errorSubCategoryCode"
                        label="Category Code"
                        // aria-label="Choose the Category Code"
                        required={true}
                        valid={true}
                        NoInitialBlankOption={false}
                        value={vm.message.errorSubCategoryCode}
                        onChange={vm.setValue}
                        options={vm.errorSubCategoryCodes}
                        errorMsg=""
                      />

                      <ComboBox
                        idAndName="moduleCode"
                        label="Module Code"
                        // aria-label="Choose the Module Code"
                        required={true}
                        valid={true}
                        NoInitialBlankOption={false}
                        value={vm.message.moduleCode}
                        onChange={vm.setValue}
                        options={vm.moduleCodes}
                        errorMsg=""
                      />

                      <ComboBox
                        idAndName="designArea"
                        label="Design Area"
                        // aria-label="Choose the Design Area"
                        required={true}
                        valid={true}
                        NoInitialBlankOption={false}
                        value={vm.message.designArea}
                        onChange={vm.setValue}
                        options={vm.designAreas}
                        errorMsg=""
                      />
                    </>
                  )}

                  {messageId ? (
                    <div className="mr-3">
                      <div>Error Category</div>
                      <div className="py-2">{vm.message.errorCategoryCode}</div>
                    </div>
                  ) : (
                    <div className="mr-3">
                      <div>Error Code</div>
                      {vm.message.errorCode ? <div className="p-2 bg-light">{vm.message.errorCode}</div> : null}
                      {vm.lookingForCode && <Spinner className="text-primary mx-3">Loading...</Spinner>}
                    </div>
                  )}
                </div>
              </div>

              <div className="d-flex flex-align-center justify-content-end flex-fill">
                <Checkbox
                  id="loggingFlag"
                  name="loggingFlag"
                  checked={vm.message.loggingFlag === 'Y'}
                  onChange={vm.setCheckboxValue}
                  label="Log Error"
                  classAdmin="my-0 nobr"
                />

                <StaticTooltip
                  tooltipMessage="Selecting the checkbox enables error logging for future investigation. This helps identify recurring errors and understand the underlyng causes."
                  tooltipSide="left"
                  id="log-error-i-button"
                  title="Log error Info"
                />
              </div>

              {messageId ? (
                <div className="flex-fill text-right">
                  <span className="nobr">Last Updated</span> {vm.message.lastChangedBy}{' '}
                  <span className="nobr">{formatDateTime(vm.message.lastChangeDate)}</span>
                </div>
              ) : null}
            </div>

            <FormGroup>
              <Label className="usa-label" htmlFor="errorShortDescription">
                Error Description
                <StaticTooltip
                  tooltipMessage="Error Description info."
                  tooltipSide="right"
                  id="errorShortDescription-i-button"
                  title="Error Description Info"
                />
              </Label>
              <Input
                type="textarea"
                required={true}
                invalid={!vm.formValid.errorShortDescription}
                id="errorShortDescription"
                name="errorShortDescription"
                value={vm.message.errorShortDescription}
                rows={2}
                onChange={vm.setTextValue}
              />
              <FormFeedback>Error Description value is required</FormFeedback>
            </FormGroup>

            <FormGroup>
              <Label className="usa-label" htmlFor="errorDetails">
                Error Details
                <StaticTooltip
                  tooltipMessage="Error Details info."
                  tooltipSide="right"
                  id="errorDetails-i-button"
                  title="Error Details Info"
                />
              </Label>
              <Input
                type="textarea"
                required={true}
                invalid={!vm.formValid.errorDetails}
                id="errorDetails"
                name="errorDetails"
                value={vm.message.errorDetails}
                rows={5}
                onChange={vm.setTextValue}
              />
              <FormFeedback>Error Details value is required</FormFeedback>
            </FormGroup>

            <FormGroup>
              <Label className="usa-label nobr" htmlFor="url">
                URL Help Link (optional)
              </Label>
              <Input id="url" name="url" value={vm.message.url ? vm.message.url : ''} onChange={vm.setTextValue} />
            </FormGroup>

            <div className="float-right">
              <Link
                to="/admin/error-messages"
                className="usa-button usa-button--outline"
                aria-label="Cancel"
                role="button"
              >
                Close
              </Link>
              <button
                className="usa-button m-0"
                type="submit"
                disabled={
                  (vm.message.errorShortDescription && vm.message.errorShortDescription.trim() === '') ||
                  (vm.message.errorDetails && vm.message.errorDetails.trim() === '') ||
                  vm.message.errorCode === ''
                }
              >
                Save
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal  */}
      {vm.deleteErrorMessageModalIsOpen && (
        <DeleteElement
          trigger={vm.deleteErrorMessageModalIsOpen}
          setTrigger={vm.toggleDeleteErrorMessageModalIsOpen}
          element="Error Message"
          name={vm.message.errorCode}
          action={vm.deleteErrorMessage}
        />
      )}
    </div>
  );
});
